import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
// Google Material Symbols - using icon names as strings
import { useAppContext } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import Header from "../Header";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";
import ProgressiveBlur from "react-progressive-blur";

const ProductRatingView: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState<string | null>(null);

  // Always show Nivea cream on rating page
  const getProductBackground = () => {
    return '/assets/images/products/nivea-cream/main.png';
  };

  const handleRatingSelect = (rating: string) => {
    setSelectedRating(rating);
    console.log("User selected rating:", rating);
    // Auto-progress to aspect selection after a short delay
    setTimeout(() => {
      navigate("/product-aspect-selection");
    }, 500);
  };


  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  const ratingOptions = [
    {
      id: "sentiment_dissatisfied",
      label: getCopy('productRating.options.notForMe'),
      icon: "sentiment_dissatisfied",
      color: "text-black"
    },
    {
      id: "okay", 
      label: getCopy('productRating.options.okay'),
      icon: "sentiment_neutral", // Material Symbols name
      color: "text-black"
    },
    {
      id: "loved",
      label: getCopy('productRating.options.loved'),
      icon: "sentiment_very_satisfied", // Material Symbols name
      color: "text-black"
    }
  ];

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="11:383"
    >
      {/* Background Image with Overlay - scaled down product image */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden"
           style={{ backgroundColor: 'white' }}>
        <img
          src={getProductBackground()}
          alt="Product background"
          className="w-auto h-1/3 object-contain blur-[2px] opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60" />
      </div>

      {/* Progressive Blur Overlay */}
      <ProgressiveBlur
        className="absolute inset-x-0 bottom-0 h-[400px]"
        intensity={80}
      />
      {/* Dark overlay on top of blur */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-b from-transparent via-transparent to-black/60 h-[600px]" />

      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-10">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - Rating Section */}
        <div className="flex-1 flex flex-col justify-end mx-20 mb-10">
          <motion.div 
            className="flex flex-col gap-10 w-full max-w-[920px] mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {/* Question */}
            <div className="text-center">
              <h2 className="font-brand-primary font-medium text-[32px] leading-[42px] text-white tracking-[-0.32px]">
                {getCopy('productRating.question')}
              </h2>
            </div>

            {/* Rating Options */}
            <div className="flex gap-10 w-full">
              {ratingOptions.map((option) => {
                return (
                  <motion.button
                    key={option.id}
                    onClick={() => handleRatingSelect(option.id)}
                    className={`flex flex-col items-center justify-center gap-2 p-4 rounded-[32px] min-h-[170px] flex-1 transition-all duration-200 ${
                      selectedRating === option.id
                        ? 'bg-white/80 border-2 border-white/50 shadow-lg'
                        : 'bg-white/60 border-2 border-white/50 hover:bg-white/70'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className={`${option.color}`}>
                      <span 
                        className="material-symbols-rounded" 
                        style={{ fontSize: '64px' }}
                      >
                        {option.icon}
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="uppercase text-[24px] text-black" style={{ fontFamily: 'var(--brand-font-button)' }}>
                        {option.label}
                      </p>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Bottom Section - Footer */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default ProductRatingView;
